import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback, useMemo, useState } from "react";
import { useFullSubmit, useNoSubmit } from "src/apps/AppFlow/pages/hooks";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import { FormFlowPage } from "src/components";
import { selectFullApplication } from "src/selectors";
import { createStyles } from "src/styles";
import { BlockFocus } from "src/utilities/react";

import { ApplicantInformation, CreditCheckConsent, LoanInformation, PropertyInformation } from "./components";

export interface SubmissionReviewProps {
  // We can assume all the values are set at this point
  value: ApplicationFlowState;

  focusRef?: Ref<HTMLDivElement>;
  blockFocus?: BlockFocus;
  onNext?: () => void;
  onBack?: () => void;
  style?: SerializedStyles;
  children?: ReactNode;
  buttonText?: string;
  overflowTextFallback?: string;
  doFullSubmit?: boolean;
}

const styles = createStyles({
  container: {
    paddingBottom: 160,
  },
  contentContainer: {
    paddingBottom: 8,
  },
});

const SubmissionReview = ({
  onNext = () => undefined,
  onBack,
  style,
  focusRef,
  value,
  children,
  buttonText = "Submit Application",
  overflowTextFallback = "Submit",
  doFullSubmit = true,
  ...props
}: SubmissionReviewProps) => {
  const app = useMemo(() => selectFullApplication(value), [value]);
  const [creditCheckConsented, setCreditCheckConsented] = useState(false);
  const validate = useCallback(() => creditCheckConsented, [creditCheckConsented]);

  const noSubmit = useNoSubmit({ onNext, validate });
  const fullSubmit = useFullSubmit({ validate });
  const handleNext = useMemo(
    () => (doFullSubmit ? fullSubmit.handleNext : noSubmit.handleNext),
    [doFullSubmit, fullSubmit.handleNext, noSubmit.handleNext]
  );
  const nextDisabled = doFullSubmit ? fullSubmit.nextDisabled : noSubmit.nextDisabled;
  const nextLoading = doFullSubmit ? fullSubmit.nextLoading : false;

  return (
    <FormFlowPage
      {...props}
      title="Final check"
      description="Please review your application, and scroll back up if anything seems incorrect."
      buttonText={buttonText}
      stickyAboveButton={
        <CreditCheckConsent
          value={creditCheckConsented}
          setValue={setCreditCheckConsented}
          location={app.property?.location}
          style={styles.contentContainer}
        />
      }
      overflowTextFallback={overflowTextFallback}
      onNext={handleNext}
      onBack={onBack}
      style={[styles.container, style]}
      ref={focusRef}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
      isStickyDesktopFooter
    >
      <div css={styles.contentContainer}>
        {app.property && <PropertyInformation property={app.property} />}
        <LoanInformation app={app} />
        {app.applicants?.map((applicant, idx) => (
          <ApplicantInformation applicant={applicant} idx={idx} app={app} key={idx} />
        ))}
      </div>

      {children}
    </FormFlowPage>
  );
};

export default memo(SubmissionReview);
